'use client'

import {
    SiteBannerPlacement,
    SiteVideo,
    getSiteVideos,
    upsertSiteVideo,
} from '@/lib/api/marketing/site-banners-api'
import { useEffect, useState } from 'react'
import { MdSave, MdSmartDisplay } from 'react-icons/md'
import { toast } from 'react-toastify'

const HERO_HOME_PLACEMENT = 'hero-home'

interface SiteVideoCardProps {
    placements: SiteBannerPlacement[]
    /** Sem permissão de escrita, o card vira somente leitura. */
    canUpdate: boolean
}

/**
 * Vídeo que fica ao lado do carrossel no topo da home. Mora junto da listagem
 * de banners porque é o mesmo espaço visual do site — separar em outra tela
 * obrigaria o operador a lembrar que os dois formam o mesmo bloco.
 */
export default function SiteVideoCard({
    placements,
    canUpdate,
}: SiteVideoCardProps) {
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [video, setVideo] = useState<SiteVideo | null>(null)

    const [provider, setProvider] = useState<'youtube' | 'mp4'>('youtube')
    const [videoId, setVideoId] = useState('')
    const [url, setUrl] = useState('')
    const [poster, setPoster] = useState('')
    const [title, setTitle] = useState('')
    const [active, setActive] = useState(true)

    const heroLabel =
        placements.find((placement) => placement.key === HERO_HOME_PLACEMENT)
            ?.label ?? 'Destaque da home'

    useEffect(() => {
        loadVideo()
    }, [])

    const loadVideo = async () => {
        setLoading(true)
        try {
            const res = await getSiteVideos()
            if (res.success && Array.isArray(res.result)) {
                const hero = res.result.find(
                    (item) => item.placement === HERO_HOME_PLACEMENT,
                )
                if (hero) {
                    setVideo(hero)
                    setProvider(hero.provider)
                    setVideoId(hero.videoId ?? '')
                    setUrl(hero.url ?? '')
                    setPoster(hero.poster ?? '')
                    setTitle(hero.title ?? '')
                    setActive(hero.active)
                }
            }
        } catch {
            toast.error('Erro ao carregar o vídeo do site')
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        if (provider === 'youtube' && !videoId.trim()) {
            toast.warning('Informe o ID ou a URL do vídeo no YouTube')
            return
        }
        if (provider === 'mp4' && !url.trim()) {
            toast.warning('Informe a URL do arquivo MP4')
            return
        }

        setSaving(true)
        try {
            const res = await upsertSiteVideo(HERO_HOME_PLACEMENT, {
                provider,
                videoId: provider === 'youtube' ? videoId.trim() : undefined,
                url: provider === 'mp4' ? url.trim() : undefined,
                poster: poster.trim() || undefined,
                title: title.trim() || undefined,
                active,
            })
            if (res.success && res.result) {
                setVideo(res.result)
                setVideoId(res.result.videoId ?? '')
                toast.success('Vídeo salvo')
            } else {
                toast.error(res.error || 'Erro ao salvar o vídeo')
            }
        } catch {
            toast.error('Erro ao salvar o vídeo')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="card bg-base-100 shadow-sm">
            <div className="card-body gap-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <MdSmartDisplay className="text-secondary text-2xl" />
                        <div>
                            <h2 className="text-lg font-bold">
                                Vídeo do {heroLabel.toLowerCase()}
                            </h2>
                            <p className="text-base-content/60 text-sm">
                                Exibido ao lado do carrossel, no topo da página
                                inicial
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <label className="label cursor-pointer gap-3">
                            <span className="label-text font-medium">
                                {active ? 'Ativo' : 'Inativo'}
                            </span>
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary"
                                checked={active}
                                disabled={!canUpdate}
                                onChange={(event) => setActive(event.target.checked)}
                            />
                        </label>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleSave}
                            disabled={saving || loading || !canUpdate}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdSave className="text-lg" />
                            )}
                            Salvar vídeo
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex h-24 items-center justify-center">
                        <span className="loading loading-spinner" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="video-provider">
                                <span className="label-text font-medium">
                                    Origem
                                </span>
                            </label>
                            <select
                                id="video-provider"
                                className="select select-bordered w-full"
                                disabled={!canUpdate}
                                value={provider}
                                onChange={(event) =>
                                    setProvider(
                                        event.target.value as 'youtube' | 'mp4',
                                    )
                                }
                            >
                                <option value="youtube">YouTube</option>
                                <option value="mp4">Arquivo MP4</option>
                            </select>
                        </div>

                        {provider === 'youtube' ? (
                            <div className="form-control w-full lg:col-span-2">
                                <label className="label pb-1" htmlFor="video-id">
                                    <span className="label-text font-medium">
                                        Vídeo do YouTube
                                    </span>
                                </label>
                                <input
                                    id="video-id"
                                    type="text"
                                    className="input input-bordered w-full"
                                    disabled={!canUpdate}
                                    placeholder="Cole a URL do vídeo ou apenas o ID"
                                    value={videoId}
                                    onChange={(event) =>
                                        setVideoId(event.target.value)
                                    }
                                />
                                <label className="label pt-1">
                                    <span className="label-text-alt text-base-content/60">
                                        A URL completa é aceita e convertida no ID
                                        automaticamente
                                    </span>
                                </label>
                            </div>
                        ) : (
                            <>
                                <div className="form-control w-full">
                                    <label className="label pb-1" htmlFor="video-url">
                                        <span className="label-text font-medium">
                                            URL do arquivo
                                        </span>
                                    </label>
                                    <input
                                        id="video-url"
                                        type="url"
                                        className="input input-bordered w-full"
                                        disabled={!canUpdate}
                                        placeholder="https://..."
                                        value={url}
                                        onChange={(event) =>
                                            setUrl(event.target.value)
                                        }
                                    />
                                </div>
                                <div className="form-control w-full">
                                    <label
                                        className="label pb-1"
                                        htmlFor="video-poster"
                                    >
                                        <span className="label-text font-medium">
                                            Imagem de capa
                                        </span>
                                    </label>
                                    <input
                                        id="video-poster"
                                        type="url"
                                        className="input input-bordered w-full"
                                        disabled={!canUpdate}
                                        placeholder="https://... (opcional)"
                                        value={poster}
                                        onChange={(event) =>
                                            setPoster(event.target.value)
                                        }
                                    />
                                </div>
                            </>
                        )}

                        <div className="form-control w-full lg:col-span-3">
                            <label className="label pb-1" htmlFor="video-title">
                                <span className="label-text font-medium">
                                    Título
                                </span>
                            </label>
                            <input
                                id="video-title"
                                type="text"
                                className="input input-bordered w-full"
                                disabled={!canUpdate}
                                placeholder="Ex.: Conheça a Mediar"
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                            />
                        </div>

                        {video && provider === 'youtube' && video.videoId && (
                            <div className="lg:col-span-3">
                                <div className="aspect-video max-w-md overflow-hidden rounded-lg">
                                    <iframe
                                        className="h-full w-full"
                                        src={`https://www.youtube.com/embed/${video.videoId}?rel=0`}
                                        title={video.title ?? 'Prévia do vídeo'}
                                        allowFullScreen
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
